import { format, isValid, parse, parseISO } from 'date-fns'
import { enUS, ko } from 'date-fns/locale'
import { isUndefined } from 'lodash-es'

import { Locale } from '@/shared/i18n'

import { isNilOrEmptyString } from '../helper'
import {
  DATE_FORMATS,
  DateFormatKey,
  DEFAULT_KO_FORMAT,
  DEFAULT_EN_FORMAT,
  DEFAULT_DAY_FORMAT,
} from './format'

// Date

export const DEFAULT_DATE = undefined

export const today = new Date()
export const getToday = () => {
  const date = new Date()

  return date
}

export const oneYearAgo = (() => {
  const oneYearAgo = new Date()
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
  oneYearAgo.setDate(oneYearAgo.getDate() + 1)

  return oneYearAgo
})()

export const oneYearLater = (() => {
  const oneYearLater = new Date()
  oneYearLater.setFullYear(oneYearLater.getFullYear() + 1)
  oneYearLater.setDate(oneYearLater.getDate() - 1)

  return oneYearLater
})()

/**
 * ISO 8601 형식의 날짜 문자열인지 확인
 * 예: 2025-12-03T12:38:41.662385981
 */
const isISO8601Format = (dateString: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateString)
}

export const formatDate = ({
  date: dateProp,
  format: formatProp = DEFAULT_KO_FORMAT,
  locale = 'ko',
}: {
  date?: Date | string
  format?: DateFormatKey
  locale?: Locale
}): string => {
  if (isNilOrEmptyString(dateProp)) {
    return ''
  }

  const localizedFormat = DATE_FORMATS[formatProp][locale]

  if (dateProp instanceof Date) {
    return format(dateProp, localizedFormat, {
      locale: locale === 'ko' ? ko : enUS,
    })
  }

  if (isISO8601Format(dateProp)) {
    const parsedDate = parseISO(dateProp)

    return format(parsedDate, localizedFormat, {
      locale: locale === 'ko' ? ko : enUS,
    })
  }

  // 일반 날짜 형식인 경우 (예: 2025.12.03)
  // 사파리 호환을 위해 .을 -로 변환
  if (dateProp.includes('.')) {
    const normalizedDateString = dateProp.replaceAll('.', '-')
    const parsedDate = parseISO(normalizedDateString)

    return format(parsedDate, localizedFormat, {
      locale: locale === 'ko' ? ko : enUS,
    })
  }

  // 이미 올바른 형식이거나 다른 형식인 경우
  const parsedDate = parseISO(dateProp)

  return format(parsedDate, localizedFormat, {
    locale: locale === 'ko' ? ko : enUS,
  })
}

export const parseDate = ({
  value,
  format,
  locale = 'ko',
}: {
  value: string
  format?: string
  locale?: Locale
}): Date | undefined => {
  const dateFormat =
    format || (locale === 'ko' ? DEFAULT_KO_FORMAT : DEFAULT_EN_FORMAT)

  try {
    const parsedDate = parse(value, dateFormat, new Date(), {
      locale: locale === 'ko' ? ko : enUS,
    })

    return isValid(parsedDate) ? parsedDate : DEFAULT_DATE
  } catch {
    return DEFAULT_DATE
  }
}

// Date Range

export type DateRange = {
  from: Date | undefined
  to?: Date | undefined
}

export type StringDateRange = {
  from?: string
  to?: string
}

const SEPARATOR = ' - '

export const DEFAULT_DATE_RANGE = undefined

export const isDateRange = (dateRange?: unknown) => {
  return (
    dateRange !== undefined &&
    dateRange !== null &&
    dateRange !== '' &&
    typeof dateRange === 'object' &&
    'from' in dateRange &&
    'to' in dateRange
  )
}

export const isSameRange = (source: DateRange, target: DateRange) => {
  if (
    formatDate({ date: source.from }) !==
    formatDate({
      date: target.from,
    })
  ) {
    return false
  }

  return formatDate({ date: source.to }) === formatDate({ date: target.to })
}

export const formatDateRange = ({
  dateRange,
  format,
  locale = 'ko',
}: {
  dateRange?: DateRange | StringDateRange
  format?: DateFormatKey
  locale?: Locale
}): string => {
  const dateFormat =
    format || (locale === 'ko' ? DEFAULT_KO_FORMAT : DEFAULT_EN_FORMAT)

  if (isNilOrEmptyString(dateRange)) {
    return ''
  }

  if (isNilOrEmptyString(dateRange.from) || isNilOrEmptyString(dateRange.to)) {
    return ''
  }

  return `${formatDate({ date: dateRange?.from, format: dateFormat, locale })}${SEPARATOR}${formatDate(
    {
      date: dateRange?.to,
      format: dateFormat,
      locale,
    },
  )}`
}

export const convertToStringDateRange = ({
  dateRange,
  format,
  locale = 'ko',
}: {
  dateRange?: DateRange
  format?: DateFormatKey
  locale?: Locale
}): StringDateRange | undefined => {
  const dateFormat =
    format || (locale === 'ko' ? DEFAULT_KO_FORMAT : DEFAULT_EN_FORMAT)

  if (isNilOrEmptyString(dateRange)) {
    return DEFAULT_DATE_RANGE
  }

  return {
    from: formatDate({ date: dateRange?.from, format: dateFormat, locale }),
    to: formatDate({ date: dateRange?.to, format: dateFormat, locale }),
  }
}

export const parseDateRange = ({
  dateRange,
  format,
  locale = 'ko',
}: {
  dateRange?: string
  format?: string
  locale?: Locale
}) => {
  const dateFormat =
    format || (locale === 'ko' ? DEFAULT_KO_FORMAT : DEFAULT_EN_FORMAT)

  if (isNilOrEmptyString(dateRange)) {
    return DEFAULT_DATE_RANGE
  }

  const [from, to] = dateRange
    .split(SEPARATOR)
    .map(date => parseDate({ value: date, format: dateFormat, locale }))

  if (isUndefined(from)) {
    return DEFAULT_DATE_RANGE
  }

  return {
    from,
    to,
  }
}

export const formatDateWithDayOfWeek = ({
  date: dateProp,
  format: formatProp = DEFAULT_KO_FORMAT,
  locale = 'ko',
}: {
  date: string | Date
  format?: DateFormatKey
  locale?: Locale
}) => {
  let date: Date

  if (dateProp instanceof Date) {
    date = dateProp
  } else if (isISO8601Format(dateProp)) {
    date = parseISO(dateProp)
  } else if (dateProp.includes('.')) {
    // 일반 날짜 형식인 경우 (예: 2025.12.03)
    const normalizedDateString = dateProp.replaceAll('.', '-')
    date = parseISO(normalizedDateString)
  } else {
    // 이미 올바른 형식이거나 다른 형식인 경우
    date = parseISO(dateProp)
  }

  const formattedDate = formatDate({
    date: date,
    format: formatProp,
    locale,
  })
  const day = format(date, DEFAULT_DAY_FORMAT, {
    locale: locale === 'ko' ? ko : enUS,
  })

  return `${formattedDate}(${day})`
}

export const isValidStringDateRange = (
  dateRange?: StringDateRange,
): dateRange is { from: string; to: string } => {
  return Boolean(dateRange?.from && dateRange?.to)
}
